import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, MessageSquare, Plus, Pencil, Trash2, DollarSign, Users, LogOut } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "../components/site/Logo";

const TABS = [
  { id: "stats", label: "Overview", icon: LayoutDashboard },
  { id: "products", label: "Products", icon: Package },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "quotes", label: "Quotes", icon: MessageSquare },
];

const EMPTY_PRODUCT = { name: "", description: "", category: "", price: "", stock: "", image: "", amazon_url: "", active: true };
const ORDER_STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];

function AdminLogin() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const res = await login(form.email, form.password);
    if (!res.ok) setError(res.error);
    else if (res.user.role !== "admin") setError("This account is not an admin.");
  };
  return (
    <div className="min-h-screen bg-[#163F25] flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login" className="w-full max-w-sm rounded-2xl bg-[#F6F5F0] p-8">
        <div className="mb-6"><Logo /></div>
        <h1 className="font-display text-2xl font-bold text-[#1A1C19]">Admin sign in</h1>
        <input data-testid="admin-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-5 w-full rounded-xl border border-[#D1CFC8] bg-white px-4 py-3 focus:outline-none focus:border-[#1F5A34]" />
        <input data-testid="admin-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-3 w-full rounded-xl border border-[#D1CFC8] bg-white px-4 py-3 focus:outline-none focus:border-[#1F5A34]" />
        {error && <p className="text-red-600 text-sm mt-2" data-testid="admin-login-error">{error}</p>}
        <button data-testid="admin-login-btn" className="mt-5 w-full py-3 rounded-full bg-[#1F5A34] text-[#F6F5F0] font-semibold">Sign in</button>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[#D1CFC8] p-6" data-testid={`stat-${label}`}>
      <Icon className="w-6 h-6 text-[#1F5A34]" />
      <p className="mt-4 font-display font-bold text-3xl text-[#1A1C19]">{value}</p>
      <p className="text-[#4A4D48] text-sm">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [editing, setEditing] = useState(null); // product being added/edited
  const isAdmin = user && user.role === "admin";

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  };
  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin]);

  if (user === null) return <div className="min-h-screen bg-[#F6F5F0]" />;
  if (!isAdmin) return <AdminLogin />;

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = { ...editing, price: parseFloat(editing.price) || 0, stock: parseInt(editing.stock) || 0 };
    delete payload.id; delete payload.created_at; delete payload._id;
    if (editing.id) await api.put(`/products/${editing.id}`, payload);
    else await api.post("/products", payload);
    setEditing(null);
    loadAll();
  };
  const deleteProduct = async (id) => { await api.delete(`/products/${id}`); loadAll(); };
  const setOrderStatus = async (id, status) => { await api.put(`/admin/orders/${id}/status`, { status }); loadAll(); };

  return (
    <div className="min-h-screen bg-[#ECE4D6]/40">
      <header className="bg-[#0E2A17] text-[#F6F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="white" />
            <span className="font-display font-bold">Admin</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="hover:text-[#A9CE8A]">View site</Link>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1 hover:text-[#A9CE8A]"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`tab-${tb.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === tb.id ? "bg-[#1F5A34] text-[#F6F5F0]" : "bg-white text-[#4A4D48] border border-[#D1CFC8]"}`}>
              <tb.icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "stats" && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-stats">
            <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(2)}`} />
            <StatCard icon={ShoppingCart} label="Paid orders" value={stats.paid_orders} />
            <StatCard icon={ShoppingCart} label="Total orders" value={stats.total_orders} />
            <StatCard icon={Package} label="Products" value={stats.products} />
            <StatCard icon={Users} label="Customers" value={stats.customers} />
            <StatCard icon={MessageSquare} label="Quotes" value={stats.quotes} />
          </div>
        )}

        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display font-bold text-2xl text-[#1A1C19]">Products</h2>
              <button onClick={() => setEditing({ ...EMPTY_PRODUCT })} data-testid="add-product-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F5A34] text-[#F6F5F0] font-semibold"><Plus className="w-4 h-4" /> Add product</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-products">
              {products.map((p) => (
                <div key={p.id} className="rounded-2xl bg-white border border-[#D1CFC8] overflow-hidden" data-testid={`admin-product-${p.id}`}>
                  <div className="h-36 bg-[#ECE4D6]">{p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}</div>
                  <div className="p-4">
                    <p className="font-semibold text-[#1A1C19]">{p.name}</p>
                    <p className="text-sm text-[#4A4D48]">${p.price.toFixed(2)} · stock {p.stock}</p>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-[#ECE4D6] text-[#1F5A34] text-sm font-medium"><Pencil className="w-3 h-3" /> Edit</button>
                      <button onClick={() => deleteProduct(p.id)} data-testid={`delete-${p.id}`} className="py-2 px-3 rounded-lg bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-4" data-testid="admin-orders">
            <h2 className="font-display font-bold text-2xl text-[#1A1C19]">Orders</h2>
            {orders.length === 0 && <p className="text-[#4A4D48]">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} className="rounded-2xl bg-white border border-[#D1CFC8] p-5" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <div>
                    <p className="font-mono text-xs text-[#4A4D48]">#{o.id.slice(0, 8)} · {o.user_email}</p>
                    <p className="text-sm text-[#1A1C19] mt-1">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#1A1C19]">${o.total.toFixed(2)}</p>
                    <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)} data-testid={`order-status-${o.id}`} className="mt-1 text-sm rounded-lg border border-[#D1CFC8] px-2 py-1 bg-[#F6F5F0]">
                      {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "quotes" && (
          <div className="space-y-4" data-testid="admin-quotes">
            <h2 className="font-display font-bold text-2xl text-[#1A1C19]">Quote requests</h2>
            {quotes.length === 0 && <p className="text-[#4A4D48]">No quotes yet.</p>}
            {quotes.map((q) => (
              <div key={q.id} className="rounded-2xl bg-white border border-[#D1CFC8] p-5" data-testid={`admin-quote-${q.id}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <p className="font-semibold text-[#1A1C19]">{q.name}{q.company ? ` · ${q.company}` : ""}</p>
                  <span className="text-xs text-[#4A4D48]">{new Date(q.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-[#4A4D48] mt-1">{q.email} {q.phone && `· ${q.phone}`} {q.province && `· ${q.province}`} {q.category && `· ${q.category}`}</p>
                {q.message && <p className="mt-2 text-sm text-[#1A1C19] bg-[#F6F5F0] rounded-lg p-3">{q.message}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveProduct} data-testid="product-form" className="w-full max-w-lg rounded-2xl bg-[#F6F5F0] p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display font-bold text-xl text-[#1A1C19] mb-4">{editing.id ? "Edit product" : "Add product"}</h3>
            <div className="space-y-3">
              <input required placeholder="Name" data-testid="pf-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full rounded-xl border border-[#D1CFC8] px-4 py-2.5 bg-white" />
              <textarea placeholder="Description" data-testid="pf-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full rounded-xl border border-[#D1CFC8] px-4 py-2.5 bg-white" rows={3} />
              <input placeholder="Category" data-testid="pf-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full rounded-xl border border-[#D1CFC8] px-4 py-2.5 bg-white" />
              <div className="grid grid-cols-2 gap-3">
                <input required type="number" step="0.01" placeholder="Price" data-testid="pf-price" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="rounded-xl border border-[#D1CFC8] px-4 py-2.5 bg-white" />
                <input type="number" placeholder="Stock" data-testid="pf-stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="rounded-xl border border-[#D1CFC8] px-4 py-2.5 bg-white" />
              </div>
              <input placeholder="Image URL" data-testid="pf-image" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} className="w-full rounded-xl border border-[#D1CFC8] px-4 py-2.5 bg-white" />
              <input placeholder="Amazon URL (optional — shows 'Buy on Amazon')" data-testid="pf-amazon" value={editing.amazon_url || ""} onChange={(e) => setEditing({ ...editing, amazon_url: e.target.value })} className="w-full rounded-xl border border-[#D1CFC8] px-4 py-2.5 bg-white" />
              <label className="flex items-center gap-2 text-sm text-[#4A4D48]"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active (visible in shop)</label>
            </div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => setEditing(null)} className="flex-1 py-3 rounded-full border border-[#D1CFC8] text-[#4A4D48] font-semibold">Cancel</button>
              <button type="submit" data-testid="pf-save" className="flex-1 py-3 rounded-full bg-[#1F5A34] text-[#F6F5F0] font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
